/**
 * One place that turns an analytics API failure into a sentence a Product Officer can act on.
 *
 * Every analytics surface funnels its errors through here — the Fresh Produce board, the
 * generic report table, the refresh button — so a report added later inherits the same
 * wording for free, which is the point of the reports framework.
 *
 * The service's own message wins when it has one: the analytics API writes its ProblemDetail
 * text for humans ("mart 'x' has not been built yet — trigger a pipeline run"), and repeating
 * it is better than paraphrasing it here and drifting. The status mapping covers the responses
 * that carry no usable body (403 from the gateway, a dead App Service, a dropped connection).
 */

export interface AnalyticsErrorOptions {
  /** Shown when neither the body nor the status says anything more specific. */
  fallback: string;
  /** Replaces the generic 403 wording with the authority this particular action needs. */
  forbidden?: string;
}

/** The analytics API uses `detail`; api_admin and Spring use `error`/`message`. */
const bodyMessage = (data: unknown): string | undefined => {
  if (!data || typeof data !== 'object') return undefined;
  const record = data as Record<string, unknown>;
  const candidate = record.detail ?? record.error ?? record.message;
  return typeof candidate === 'string' && candidate.trim() ? candidate.trim() : undefined;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const describeAnalyticsError = (error: any, options: AnalyticsErrorOptions): string => {
  const response = error?.response;

  // No response at all — the request never completed (offline, DNS, timeout, CORS). Saying
  // "failed" here would point the reader at the data when the problem is the connection.
  if (!response) {
    return 'Could not reach the analytics service — check your connection and try again';
  }

  const status: number = response.status;
  const fromBody = bodyMessage(response.data);

  switch (status) {
    case 401:
      // axiosClient's interceptor has already logged the user out and toasted; this is the
      // half that says which action was lost, so the two read as one story.
      return 'Your session expired before this could load — sign in again';
    case 403:
      return options.forbidden ?? 'You do not have permission to view this analytics data';
    case 409:
      // Handled as a normal outcome by AnalyticsService.triggerRefresh; here for anything
      // that reaches the catch by another route.
      return 'A sync is already running — wait for it to finish';
    case 503:
      return fromBody ?? 'This report has not been built yet — run a sync first';
    default:
      break;
  }

  // 5xx bodies are stack traces and framework noise as often as they are messages.
  if (status >= 500) {
    return `The analytics service is unavailable (HTTP ${status}) — try again shortly`;
  }

  return fromBody ?? options.fallback;
};
